// Super-admin coupon management: list, add, edit, toggle active, delete.
// The coupons returned by GET /api/coupons feed every sale-recording form,
// the sales filter bars, and the super-analytics discount chips, so the
// user can introduce or retire a coupon without a code change.
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';
import { Tag, Plus, Pencil, Trash2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const empty = { label: '', active: true };

export default function SuperCoupons() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/coupons?include_inactive=1`);
      setList(res.data || []);
    } catch (e) {
      toast.error('فشل جلب الكوبونات');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openAdd = () => { setEditingId(null); setForm(empty); setOpen(true); };
  const openEdit = (c) => {
    setEditingId(c.id);
    setForm({ label: c.label, active: !!c.active });
    setOpen(true);
  };

  const save = async () => {
    if (!form.label.trim()) return toast.error('الاسم مطلوب');
    try {
      const payload = { label: form.label.trim(), active: !!form.active };
      if (editingId) {
        await axios.put(`${API_URL}/api/super/coupons/${editingId}`, payload);
        toast.success('تم تحديث الكوبون');
      } else {
        await axios.post(`${API_URL}/api/super/coupons`, payload);
        toast.success('تم إضافة الكوبون');
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'فشل الحفظ');
    }
  };

  const del = async (c) => {
    if (!window.confirm(`حذف الكوبون "${c.label}" نهائياً؟ المبيعات القديمة ستبقى بقيمتها لكن الكوبون لن يظهر للموظفين.`)) return;
    try {
      await axios.delete(`${API_URL}/api/super/coupons/${c.id}`);
      toast.success('تم الحذف');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'فشل الحذف'); }
  };

  const toggle = async (c) => {
    try {
      await axios.put(`${API_URL}/api/super/coupons/${c.id}`, { active: !c.active });
      load();
    } catch (e) { toast.error('فشل التحديث'); }
  };

  return (
    <div className="space-y-6" data-testid="super-coupons-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-3xl sm:text-4xl font-black text-primary tracking-tight flex items-center gap-2">
            <Tag className="h-7 w-7" />
            كوبونات الخصم
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            أضف/عدّل/احذف الكوبونات التي تظهر للموظفين عند تسجيل المبيعات، وفي فلاتر التقارير والتحليلات.
          </p>
        </div>
        <Button onClick={openAdd} data-testid="add-coupon-btn" className="gap-2">
          <Plus className="h-4 w-4" />
          كوبون جديد
        </Button>
      </div>

      <Card className="border-border overflow-hidden">
        {loading ? (
          <div className="py-12 text-center text-muted-foreground">جاري التحميل...</div>
        ) : list.length === 0 ? (
          <div className="py-12 text-center text-muted-foreground">لا توجد كوبونات بعد</div>
        ) : (
          <div className="divide-y divide-border">
            {list.map((c) => (
              <div key={c.id} className="flex items-center gap-3 px-5 py-3 hover:bg-accent/30" data-testid={`coupon-row-${c.id}`}>
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-md bg-brand_blue/10 text-brand_blue font-bold font-numbers">
                  <Tag className="h-3.5 w-3.5" />
                  {c.label}
                </span>
                <div className="ms-auto flex items-center gap-3">
                  <label className="flex items-center gap-2 cursor-pointer text-xs">
                    <Switch checked={!!c.active} onCheckedChange={() => toggle(c)} data-testid={`coupon-toggle-${c.id}`} />
                    <span className={c.active ? 'text-success font-bold' : 'text-muted-foreground'}>
                      {c.active ? 'فعّال' : 'معطّل'}
                    </span>
                  </label>
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(c)} data-testid={`edit-coupon-${c.id}`}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => del(c)} data-testid={`delete-coupon-${c.id}`}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent dir="rtl" className="max-w-md" data-testid="coupon-dialog">
          <DialogHeader>
            <DialogTitle>{editingId ? 'تعديل الكوبون' : 'إضافة كوبون جديد'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>اسم الكوبون</Label>
              <Input
                value={form.label}
                onChange={(e) => setForm({ ...form, label: e.target.value })}
                placeholder="مثال: PD50"
                className="text-right"
                data-testid="coupon-label-input"
              />
              <p className="text-[11px] text-muted-foreground">
                سيظهر هذا الاسم للموظفين في نموذج تسجيل البيع وفي فلاتر التقارير.
              </p>
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <Switch
                checked={!!form.active}
                onCheckedChange={(v) => setForm({ ...form, active: v })}
                data-testid="coupon-active-switch"
              />
              <span className="text-sm font-bold">فعّال (يظهر للموظفين)</span>
            </label>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={() => setOpen(false)}>إلغاء</Button>
            <Button onClick={save} data-testid="save-coupon-btn">حفظ</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
